// Miky Motorsport - cista zavodni logika (razeni, eliminace, rubber-band, konec zavodu).
// Bez THREE/Rapier - operuje nad prostymi objekty/poli, takze je testovatelne v Node.
// race.js tyto funkce importuje a vola, aby chovani hry zustalo identicke.
import { clamp } from './physics/tires.js';
import { sectorTimes } from './sectors.js';

// Poradi AI definic; pripadny pojmenovany rival (z eventu) na celo pole.
// aiDefs = pole {name,...}; rivalName = jmeno rivala nebo prazdne.
export function orderAiDefs(aiDefs, rivalName) {
  if (!rivalName) return aiDefs;
  const idx = aiDefs.findIndex((d) => d.name === rivalName);
  if (idx <= 0) return aiDefs;
  return [aiDefs[idx], ...aiDefs.filter((_, i) => i !== idx)];
}

// Index prvniho checkpointu za arc-length s (jinak 0 = wrap na cp0, kdyz je s za poslednim cp).
export function firstCpAfter(checkpoints, s) {
  for (let i = 0; i < checkpoints.length; i++) { if (checkpoints[i].s > s) return i; }
  return 0;
}

// Serad auta dle pozice v zavode a priradi car.position (1-based). Vraci serazene pole.
// Poradi pravidel:
//  - vyrazena (eliminace) vzdy dole; pozdeji vyrazene vys (vetsi _elimAt = vys).
//    Vyjimka: dojete auto se NEbere jako vyrazene (finished ma prednost - F-404).
//  - dojeta auta nad aktivnimi; mezi sebou dle finishTime (driv = vys).
//  - aktivni dle progress sestupne.
export function rankCars(cars) {
  const sorted = [...cars].sort((a, b) => {
    // finished ma prednost pred eliminated (auto, ktere stihlo dojet, neni "vyrazene")
    const aElim = a.eliminated && !a.finished;
    const bElim = b.eliminated && !b.finished;
    // vyrazena auta (eliminace) vzdy dole; pozdeji vyrazene vys
    if (aElim || bElim) {
      if (aElim && bElim) return b._elimAt - a._elimAt;
      return aElim ? 1 : -1;
    }
    if (a.finished && b.finished) return a.finishTime - b.finishTime;
    if (a.finished) return -1;
    if (b.finished) return 1;
    return b.progress - a.progress;
  });
  sorted.forEach((c, i) => (c.position = i + 1));
  return sorted;
}

// Rubber-band skalovani cilove rychlosti AI dle odstupu od hrace. Clamp [0.9, 1.08].
// Kdyz hrac dojel (playerFinished), AI uz nedohani -> scale 1.0.
export function rubberScale(playerProgress, carProgress, rubberK, playerFinished) {
  if (playerFinished) return 1.0;
  const diff = playerProgress - carProgress;
  return clamp(1 + diff * rubberK, 0.9, 1.08);
}

// Eliminace: vrat aktivni auto s nejnizsim progress, nebo null kdyz aktivnich <= 1.
// Dojete auto se nebere jako aktivni (finished i eliminated jsou vyrazeny z vyberu).
export function pickEliminated(cars) {
  const active = cars.filter((c) => !c.eliminated && !c.finished);
  if (active.length <= 1) return null;
  let last = active[0];
  for (const c of active) if (c.progress < last.progress) last = c;
  return last;
}

// Prejezd checkpointu / pocitani kol - ciste jadro z race.js `_trackCar`.
// Pracuje nad prostym `state` (pole, ktera race.js cte/zapisuje na aute):
//   { nextCp, _sidePrev, started, lapStartTime, lap, lapTimes, bestLap, finished, finishTime }
// `checkpoints` = pole cp {x,z,tx,tz,nx,nz,halfWidth}; cp = checkpoints[state.nextCp].
// x,z = aktualni pozice auta; raceTime = cas zavodu; numCheckpoints = pocet cp;
// laps = pocet kol pro dokonceni; eliminated = je-li auto vyrazene (eliminace).
// Funkce mutuje `state` (stejne jako puvodni `_trackCar` mutoval car) a vraci
//   { justFinished } - true prave kdyz auto v tomto kroku dojelo (caller nastavi firstFinishTime).
// Geometrie:
//   side = tangencialni projekce (smer trati), lat = normalova projekce (stranou).
//   Prejezd = _sidePrev<0 && side>=0 && |lat| < halfWidth+9 (fyzicky koridor ke stene).
//   cp0 prvni prejezd: jen started + lapStartTime (bez inkrementu kola).
//   cp0 dalsi prejezd: zapis lapTime, bestLap=min, lapStartTime=raceTime, lap++, pripadny dojezd.
//   Dojeta ANI vyrazena auta uz kola/dojezd nepocitaji (jedou dal jen pro progress/razeni).
//   Po prejezdu se nextCp posune (modulo numCheckpoints) a side se prepocita proti NOVEMU cp.
export function advanceCheckpoint(state, checkpoints, x, z, raceTime, numCheckpoints, laps, eliminated, secBoundaries = null) {
  let cp = checkpoints[state.nextCp];
  let side = (x - cp.x) * cp.tx + (z - cp.z) * cp.tz;
  const lat = (x - cp.x) * cp.nx + (z - cp.z) * cp.nz;
  let justFinished = false;
  if (state._sidePrev !== undefined && state._sidePrev < 0 && side >= 0 && Math.abs(lat) < cp.halfWidth + 9) {
    const crossedCp = state.nextCp;
    if (crossedCp === 0 && !state.finished && !eliminated) {
      if (!state.started) {
        state.started = true;
        state.lapStartTime = raceTime;
        if (secBoundaries) state._secSplits = [];
      } else {
        const lt = raceTime - state.lapStartTime;
        const wasBest = lt < state.bestLap; // B3: sektory nejlepsiho kola
        state.lapTimes.push(lt);
        state.bestLap = Math.min(state.bestLap, lt);
        if (secBoundaries) {
          const secs = sectorTimes(state._secSplits || [], lt);
          state.lastSectors = secs;
          if (wasBest) state.bestSectors = secs.slice();
          state._secSplits = [];
        }
        state.lapStartTime = raceTime;
        state.lap++;
        if (state.lap >= laps && !state.finished) {
          state.finished = true;
          state.finishTime = raceTime;
          justFinished = true;
        }
      }
    } else if (secBoundaries && state.started && !state.finished && !eliminated && secBoundaries.indexOf(crossedCp) !== -1) {
      // B3: prejezd sektorove hranice (ne cp0) -> zaznam casu (relativni k zacatku kola)
      if (!state._secSplits) state._secSplits = [];
      state._secSplits.push(raceTime - state.lapStartTime);
    }
    state.nextCp = (state.nextCp + 1) % numCheckpoints;
    cp = checkpoints[state.nextCp];
    side = (x - cp.x) * cp.tx + (z - cp.z) * cp.tz;
  }
  state._sidePrev = side;
  return { justFinished };
}

// Konec zavodu dle rezimu. state = prosty objekt:
//  {mode, playerFinished, playerEliminated, allFinished, activeLeft, elimOccurred,
//   firstFinishTime, raceTime, finishGrace, maxRaceTime}
// Vraci true pokud ma zavod skoncit.
export function raceDone(state) {
  const {
    mode, playerFinished, playerEliminated, allFinished, activeLeft, elimOccurred,
    firstFinishTime, raceTime, finishGrace, maxRaceTime,
  } = state;
  let done = false;
  if (mode === 'elimination') {
    // [F-403] "posledni zbyvajici" (activeLeft<=1) ukoncuje zavod az kdyz uz probehla
    // aspon jedna eliminace - jinak by zavod s <2 jezdci skoncil okamzite (activeLeft<=1
    // od startu). Normalni multi-car zavod: do okamziku activeLeft<=1 vzdy probehly
    // eliminace, takze chovani je identicke.
    if (playerEliminated || playerFinished || (elimOccurred && activeLeft <= 1)) done = true;
  } else {
    // jakmile dojede HRAC, konec hned (zadne cekani na soupere); fallback pri DNF hrace.
    const sinceFirst = firstFinishTime != null ? raceTime - firstFinishTime : 0;
    if (playerFinished || allFinished || (firstFinishTime != null && sinceFirst > finishGrace)) done = true;
  }
  // Tvrdy strop casu - posledni pojistka proti softlocku (nikdo nikdy nedojede).
  if (raceTime > maxRaceTime) done = true;
  return done;
}
